import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner, Tabs, Tab } from 'react-bootstrap';
import Plot from 'react-plotly.js';
import { FaCheckCircle, FaTimesCircle, FaStar } from 'react-icons/fa';
import { handleTokenAndPostRequest, setMetaTags } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';
import RatingComponent from '../components/RatingComponent';
import SuburbCard from '../components/SuburbCard';

const PostcodeProfile = () => {
  const [postcodeData, setPostcodeData] = useState(null);
  const [postcodeMonthlyData, setPostcodeMonthlyData] = useState(null);
  const [postcodePropertyTypeData, setPostcodePropertyTypeData] = useState(null);
  const [rentPostcodePropertyTypeData, setRentPostcodePropertyTypeData] = useState(null);
  const [soldPostcodePropertyTypeData, setSoldPostcodePropertyTypeData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const [suburbsInPostcodeData, setSuburbsInPostcodeData] = useState(null);
  const [loadingSuburbs, setLoadingSuburbs] = useState(false);

  const { postcode } = useParams();
  const { agent_slug, agentDetails } = useAgentDetails();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch postcode summary data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/postcode`,
          {
            postcodes: [postcode],
            agent_slug: agent_slug,
          },
          {
            'Content-Type': 'application/json',
          }
        );
        setPostcodeData(response.data && response.data.length > 0 ? response.data[0] : null);
      } catch (error) {
        console.error("Error fetching postcode data", error);
      }
    };
    fetchData();
  }, [postcode, agent_slug]);

  // Fetch suburb details for suburbs_in_postcode
  useEffect(() => {
    const fetchSuburbs = async () => {
      if (!postcodeData || !postcodeData.suburbs_in_postcode) return;
      setLoadingSuburbs(true);
      try {
        let suburbIds = postcodeData.suburbs_in_postcode;
        if (typeof suburbIds === 'string') {
          try {
            suburbIds = JSON.parse(suburbIds);
          } catch (e) {
            suburbIds = [];
          }
        }
        if (!Array.isArray(suburbIds) || suburbIds.length === 0) {
          setSuburbsInPostcodeData([]);
          setLoadingSuburbs(false);
          return;
        }
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/suburb`,
          {
            suburb_ids: suburbIds,
            agent_slug: agent_slug,
            generate_llm: false
          },
          {
            'Content-Type': 'application/json',
          }
        );
        setSuburbsInPostcodeData(response.data);
      } catch (error) {
        setSuburbsInPostcodeData([]);
        console.error("Error fetching suburbs in postcode", error);
      }
      setLoadingSuburbs(false);
    };
    fetchSuburbs();
  }, [postcodeData, agent_slug]);

  // Fetch property type summary
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/postcode-property-type-last-month-summary`,
          {
            postcode: postcode,
            agent_slug: agent_slug,
          },
          {
            'Content-Type': 'application/json',
          }
        );
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        setPostcodePropertyTypeData(data);
        setSoldPostcodePropertyTypeData(data['Sold']);
        setRentPostcodePropertyTypeData(data['Rent']);
      } catch (error) {
        console.error("Error fetching postcode property type data", error);
      }
    };
    fetchData();
  }, [postcode, agent_slug]);

  // Fetch monthly data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/postcode-monthly`,
          {
            postcode: postcode,
            agent_slug: agent_slug,
          },
          {
            'Content-Type': 'application/json',
          }
        );
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        setPostcodeMonthlyData(data);
      } catch (error) {
        console.error("Error fetching postcode monthly data", error);
      }
    };
    fetchData();
  }, [postcode, agent_slug]);

  useEffect(() => {
    if (agentDetails && postcodeData) {
      const title = `Postcode ${postcodeData.postcode} Real Estate Insights by ${agentDetails.agency_name_short}`;
      const description = `Explore in-depth real estate insights and market trends for postcode ${postcodeData.postcode}, ${postcodeData.state}. Discover detailed property values, recent sales data, rental market analysis, and comprehensive postcode reports. Stay informed with the latest updates on median prices, days on market, and property types. Brought to you by ${agentDetails.agency_name_short}, your trusted source for real estate information.`;
      const keywords = `Real Estate, Postcode ${postcodeData.postcode} Properties, ${postcodeData.state} Market Trends, Property Values, ${agentDetails.agency_name_short}, Postcode Reports, Median Prices, Rental Market, Sales Data, Property Types, Real Estate Insights, ${postcodeData.postcode} Real Estate, ${postcodeData.postcode} Housing Market, ${postcodeData.state} Real Estate`;
      const url = `https://homeprice.site/postcode-profile/${postcode}`;
      const image = agentDetails.landing_background_url;
      setMetaTags({ title, description, keywords, url, image });
    }
  }, [agentDetails, postcodeData, postcode]);

  if (!agentDetails || !postcodeData || !postcodePropertyTypeData || !postcodeMonthlyData) {
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        <Spinner animation="border" />
      </div>
    );
  }

  // --- LLM fields parsing ---
  let ratings = null;
  let ratingExplanations = null;
  let upsides = [];
  let downsides = [];
  let postcode_summary = postcodeData.postcode_summary;

  try {
    ratings = postcodeData.ratings ? JSON.parse(postcodeData.ratings) : null;
  } catch (e) {
    ratings = null;
  }
  try {
    ratingExplanations = postcodeData.rating_explanations ? JSON.parse(postcodeData.rating_explanations) : null;
  } catch (e) {
    ratingExplanations = null;
  }
  try {
    upsides = postcodeData.upsides ? JSON.parse(postcodeData.upsides) : [];
  } catch (e) {
    upsides = [];
  }
  try {
    downsides = postcodeData.downsides ? JSON.parse(postcodeData.downsides) : [];
  } catch (e) {
    downsides = [];
  }

  const {
    median_sold_days_on_market_filled,
    median_sold_price_filled,
    prior_median_sold_days_on_market,
    prior_median_sold_price,
    prior_sold_properties,
    median_sold_days_on_market_percent_change_text,
    median_sold_price_percent_change_text,
    sold_properties_percent_change_text,
    sold_properties,
    median_rent_days_on_market_filled,
    median_rent_price_filled,
    prior_median_rent_days_on_market,
    prior_median_rent_price,
    prior_rent_properties,
    median_rent_days_on_market_percent_change_text,
    median_rent_price_percent_change_text,
    rent_properties_percent_change_text,
    rent_properties,
    reporting_month,
  } = postcodeData;

  const median_sold_price_display =
    typeof median_sold_price_filled === 'number' && median_sold_price_filled !== null
      ? `$${median_sold_price_filled.toLocaleString('en-US')}`
      : 'N/A';

  const sold_days_on_market_delta =
    typeof median_sold_days_on_market_filled === 'number' &&
    typeof prior_median_sold_days_on_market === 'number' &&
    prior_median_sold_days_on_market !== 0 &&
    median_sold_days_on_market_filled !== null &&
    prior_median_sold_days_on_market !== null
      ? ((median_sold_days_on_market_filled - prior_median_sold_days_on_market) / prior_median_sold_days_on_market * 100).toFixed(0)
      : null;

  const sold_price_delta =
    typeof median_sold_price_filled === 'number' &&
    typeof prior_median_sold_price === 'number' &&
    prior_median_sold_price !== 0 &&
    median_sold_price_filled !== null &&
    prior_median_sold_price !== null
      ? ((median_sold_price_filled - prior_median_sold_price) / prior_median_sold_price * 100).toFixed(0)
      : null;

  const sold_properties_delta =
    typeof sold_properties === 'number' &&
    typeof prior_sold_properties === 'number' &&
    prior_sold_properties !== 0 &&
    sold_properties !== null &&
    prior_sold_properties !== null
      ? ((sold_properties - prior_sold_properties) / prior_sold_properties * 100).toFixed(0)
      : null;

  const median_rent_price_display =
    typeof median_rent_price_filled === 'number' && median_rent_price_filled !== null
      ? `$${median_rent_price_filled.toLocaleString('en-US')}`
      : 'N/A';

  const rent_days_on_market_delta =
    typeof median_rent_days_on_market_filled === 'number' &&
    typeof prior_median_rent_days_on_market === 'number' &&
    prior_median_rent_days_on_market !== 0 &&
    median_rent_days_on_market_filled !== null &&
    prior_median_rent_days_on_market !== null
      ? ((median_rent_days_on_market_filled - prior_median_rent_days_on_market) / prior_median_rent_days_on_market * 100).toFixed(0)
      : null;

  const rent_price_delta =
    typeof median_rent_price_filled === 'number' &&
    typeof prior_median_rent_price === 'number' &&
    prior_median_rent_price !== 0 &&
    median_rent_price_filled !== null &&
    prior_median_rent_price !== null
      ? ((median_rent_price_filled - prior_median_rent_price) / prior_median_rent_price * 100).toFixed(0)
      : null;

  const rent_properties_delta =
    typeof rent_properties === 'number' &&
    typeof prior_rent_properties === 'number' &&
    prior_rent_properties !== 0 &&
    rent_properties !== null &&
    prior_rent_properties !== null
      ? ((rent_properties - prior_rent_properties) / prior_rent_properties * 100).toFixed(0)
      : null;

  const formatDataForPlotlyBar = (data, column_to_plot) => {
    if (!data) {
      return [];
    }
    const propertyTypes = [...new Set(data.map(item => item.property_type_display))];
    return propertyTypes.map(propertyType => {
      const filteredData = data.filter(item => item.property_type_display === propertyType);
      return {
        x: filteredData.map(item => item.reporting_month),
        y: filteredData.map(item => item[column_to_plot]),
        type: 'bar',
        name: propertyType
      };
    });
  };

  const formatDataForPlotly = (data, column_to_plot) => {
    if (!data) {
      return [];
    }
    const propertyTypes = [...new Set(data.map(item => item.property_type_display))];
    return propertyTypes.map(propertyType => {
      const filteredData = data.filter(item => item.property_type_display === propertyType);
      return {
        x: filteredData.map(item => item.reporting_month),
        y: filteredData.map(item => item[column_to_plot]),
        type: 'scatter',
        mode: 'lines+markers',
        name: propertyType
      };
    });
  };

  // SuburbsList component for postcode
  const SuburbsList = ({ suburbs, agent_slug }) => {
    if (!suburbs || !suburbs.length) {
      return <div>No suburb data found for this postcode.</div>;
    }
    // Only show first 3 on mobile
    const displaySuburbs = isMobile ? suburbs.slice(0, 3) : suburbs;
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        {displaySuburbs.map((suburb, idx) => (
          <SuburbCard key={suburb.suburb_id || idx} suburb={suburb} agent_slug={agent_slug} />
        ))}
      </div>
    );
  };

  return (
    <div>
      {agentDetails && postcodeData && (
        <div
          id='postcode-profile-hero'
          style={{
            height: '40vh',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            backgroundColor: '#f0f0f0',
            backgroundImage: `url(${agentDetails.landing_background_url})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundAttachment: window.innerWidth > 768 ? 'fixed' : 'scroll',
            backgroundSize: 'cover',
          }}
        >
          <p style={{ marginBottom: '15px', fontSize: 'calc(20px + 4vw)' }}>
            Postcode {postcodeData.postcode}
          </p>
          <p style={{ marginBottom: '15px', fontSize: 'calc(10px + 1.5vw)' }}>
            {postcodeData.state}
          </p>
        </div>
      )}
      <Container className="postcode-profile-page" style={{ paddingTop: '40px', maxWidth: '800px' }}>
        {/* --- LLM Section --- */}
        {postcode_summary && postcode_summary.trim() !== "" && (
          <div>
            <h2>Postcode Overview</h2>
            <p>{postcode_summary}</p>
            {ratings && ratingExplanations && (
              <RatingComponent
                data={ratings}
                explanations={ratingExplanations}
              />
            )}
            <h5>Upsides</h5>
            <ul style={{ listStyleType: 'none', paddingLeft: '2rem', paddingRight: '2rem' }}>
              {Array.isArray(upsides) && upsides.length > 0 ? (
                upsides.map((upside, index) => {
                  const [title, ...description] = upside.split(':');
                  return (
                    <li key={index}>
                      <FaCheckCircle style={{ color: 'green', marginRight: '8px' }} />
                      <strong>{title}:</strong>{description.join(':')}
                    </li>
                  );
                })
              ) : (
                <li>No upsides available.</li>
              )}
            </ul>
            <h5>Downsides</h5>
            <ul style={{ listStyleType: 'none', paddingLeft: '2rem', paddingRight: '2rem' }}>
              {Array.isArray(downsides) && downsides.length > 0 ? (
                downsides.map((downside, index) => {
                  const [title, ...description] = downside.split(':');
                  return (
                    <li key={index}>
                      <FaTimesCircle style={{ color: 'red', marginRight: '8px' }} />
                      <strong>{title}:</strong>{description.join(':')}
                    </li>
                  );
                })
              ) : (
                <li>No downsides available.</li>
              )}
            </ul>
          </div>
        )}
        <h2>Summary Last Month</h2>
        <Row className="justify-content-md-center" style={{ alignItems: 'center' }}>
          <h5>Sale Market</h5>
          <Col xs={12} md={4}>
            <div style={{ textAlign: 'center' }}>
              <span>Median Days on Market</span>
              <h1>{median_sold_days_on_market_filled}</h1>
              <div style={{ color: median_sold_days_on_market_percent_change_text === '0%' ? 'gray' : sold_days_on_market_delta > 0 ? 'green' : 'red' }}>
                <strong>{median_sold_days_on_market_percent_change_text}</strong>
              </div>
            </div>
          </Col>
          <Col xs={12} md={4} style={{ padding: '30px 0' }}>
            <div style={{ textAlign: 'center' }}>
              <span>Sold Properties</span>
              <h1>{sold_properties}</h1>
              <div style={{ color: sold_properties_percent_change_text === '0%' ? 'gray' : sold_properties_delta > 0 ? 'green' : 'red' }}>
                <strong>{sold_properties_percent_change_text}</strong>
              </div>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ textAlign: 'center' }}>
              <span>Median Sold Price</span>
              <h1>{median_sold_price_display}</h1>
              <div style={{ color: median_sold_price_percent_change_text === '0%' ? 'gray' : sold_price_delta > 0 ? 'green' : 'red' }}>
                <strong>{median_sold_price_percent_change_text}</strong>
              </div>
            </div>
          </Col>
        </Row>
        <Row className="justify-content-md-center" style={{ alignItems: 'center' }}>
          <h5>Rental Market</h5>
          <Col xs={12} md={4}>
            <div style={{ textAlign: 'center' }}>
              <span>Median Days on Market</span>
              <h1>{median_rent_days_on_market_filled}</h1>
              <div style={{ color: median_rent_days_on_market_percent_change_text === '0%' ? 'gray' : rent_days_on_market_delta > 0 ? 'green' : 'red' }}>
                <strong>{median_rent_days_on_market_percent_change_text}</strong>
              </div>
            </div>
          </Col>
          <Col xs={12} md={4} style={{ padding: '30px 0' }}>
            <div style={{ textAlign: 'center' }}>
              <span>Rented Properties</span>
              <h1>{rent_properties}</h1>
              <div style={{ color: rent_properties_percent_change_text === '0%' ? 'gray' : rent_properties_delta > 0 ? 'green' : 'red' }}>
                <strong>{rent_properties_percent_change_text}</strong>
              </div>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ textAlign: 'center' }}>
              <span>Median Rent Price</span>
              <h1>{median_rent_price_display}</h1>
              <div style={{ color: median_rent_price_percent_change_text === '0%' ? 'gray' : rent_price_delta > 0 ? 'green' : 'red' }}>
                <strong>{median_rent_price_percent_change_text}</strong>
              </div>
            </div>
          </Col>
        </Row>
        <p>Note: Median sold price reflects the postcode as a whole and may not match the uniqueness of your property. For a more accurate valuation estimate for your property, click below.</p>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
          <Button as={Link} to={`/property-search-live/${agentDetails.landing_page_slug}`} variant="primary" className="submit-btn" style={{ backgroundColor: agentDetails.agent_button_colour, borderColor: agentDetails.agent_button_colour }}>
            Free Property Valuation Tool
          </Button>
        </div>
        <br />
        <br />
        <h2>Postcode Property Type Summary Last Month</h2>
        <p>The data below summarises the results over the last month by property type and bedrooms.</p>
        <Tabs defaultActiveKey="sale" fill>
          <Tab eventKey="sale" title="Sale Market">
            {!soldPostcodePropertyTypeData || soldPostcodePropertyTypeData.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2.5em' }}>
                <p style={{ textAlign: 'center', fontSize: '1.1em', color: '#555' }}>No sold properties last month</p>
                <br />
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: 'Arial, sans-serif', marginTop: '2em' }}>
                  <thead style={{ backgroundColor: 'transparent' }}>
                    <tr>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        PROPERTY TYPE
                      </th>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        BEDS
                      </th>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        SOLD PRICE
                      </th>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        SOLD LAST MONTH
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {soldPostcodePropertyTypeData.map((property, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.property_type_display}
                        </td>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.bedrooms_string}
                        </td>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.display_price_short}
                        </td>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.properties_last_month}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Tab>
          <Tab eventKey="rent" title="Rental Market">
            {!rentPostcodePropertyTypeData || rentPostcodePropertyTypeData.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2.5em' }}>
                <p style={{ textAlign: 'center', fontSize: '1.1em', color: '#555' }}>No rented properties last month</p>
                <br />
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: 'Arial, sans-serif', fontSize: '100%', marginTop: '2em' }}>
                  <thead style={{ backgroundColor: 'transparent' }}>
                    <tr>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        PROPERTY TYPE
                      </th>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        BEDS
                      </th>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        RENTED PRICE
                      </th>
                      <th style={{ textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '0.6em', color: '#333', background: 'transparent', borderBottom: '1px solid #dddddd' }}>
                        RENTED LAST MONTH
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rentPostcodePropertyTypeData.map((property, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.property_type_display}
                        </td>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.bedrooms_string}
                        </td>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.display_price_short}
                        </td>
                        <td style={{ border: '1px solid #dddddd', textAlign: 'center', padding: '8px', fontSize: '0.8em', color: '#333', whiteSpace: 'nowrap' }}>
                          {property.properties_last_month}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <br />
              </div>
            )}
          </Tab>
        </Tabs>
        <br />
        <br />
        <h2>Median Prices Over Time</h2>
        {postcodeMonthlyData && agentDetails && (
          <Row className="justify-content-md-center">
            <Col>
              <div style={{ overflowX: 'auto' }}>
                <Plot
                  data={formatDataForPlotly(postcodeMonthlyData.data, 'median_sold_price_filled')}
                  layout={{
                    xaxis: {
                      title: 'Month',
                      tickformat: '%Y-%m',
                      type: 'date',
                    },
                    yaxis: {
                      title: 'Median Sold Price ($)',
                      tickprefix: '$',
                      range: [0, Math.max(...postcodeMonthlyData?.data?.flatMap(d => d.median_sold_price_filled) || [0])],
                    },
                    autosize: true,
                  }}
                  config={{ displayModeBar: false }}
                  useResizeHandler={true}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        )}
        <br />
        <br />
        <h2>Median Days on Market Over Time</h2>
        {postcodeMonthlyData && agentDetails && (
          <Row className="justify-content-md-center">
            <Col>
              <div style={{ overflowX: 'auto' }}>
                <Plot
                  data={formatDataForPlotly(postcodeMonthlyData.data, 'median_sold_days_on_market_filled')}
                  layout={{
                    xaxis: {
                      title: 'Month',
                      tickformat: '%Y-%m',
                      type: 'date',
                    },
                    yaxis: {
                      title: 'Median Days on Market',
                      range: [0, Math.max(...postcodeMonthlyData?.data?.flatMap(d => d.median_sold_days_on_market_filled) || [0])],
                    },
                    autosize: true,
                  }}
                  config={{ displayModeBar: false }}
                  useResizeHandler={true}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        )}
        <br />
        <br />
        <h2>Sold Properties Over Time</h2>
        {postcodeMonthlyData && agentDetails && (
          <Row className="justify-content-md-center">
            <Col>
              <div style={{ overflowX: 'auto' }}>
                <Plot
                  data={formatDataForPlotlyBar(postcodeMonthlyData.data, 'sold_properties')}
                  layout={{
                    barmode: 'stack',
                    xaxis: {
                      title: 'Month',
                      tickformat: '%Y-%m',
                      type: 'date',
                    },
                    yaxis: {
                      title: 'Sold Properties',
                    },
                    autosize: true,
                  }}
                  config={{ displayModeBar: false }}
                  useResizeHandler={true}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        )}
        {/* --- Suburbs in this postcode --- */}
        <div style={{ marginBottom: '80px'}}>
            <h3>Suburbs in this Postcode</h3>
            {loadingSuburbs || !suburbsInPostcodeData ? (
                <div style={{
                position: 'relative',
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
                }}>
                <Spinner animation="border" />
                </div>
            ) : (
                <SuburbsList suburbs={suburbsInPostcodeData} agent_slug={agent_slug}/>
        )}
        </div>        
      </Container>
    </div>
  );
};

export default PostcodeProfile;